// <!-- og-image v1 -->
import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import ManifestoPage from "./pages/ManifestoPage";
import AdminPage from "./pages/AdminPage";
import PrivacyPage from "./pages/PrivacyPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Phosphor Icon Component
const PhosphorIcon = ({ name, className = "" }) => (
  <i className={`ph ph-${name} ${className}`}></i>
);

// Cookie Consent Banner Component
const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      setShowBanner(true);
    } else if (consent === "accepted") {
      loadPostHog();
    }
  }, []);

  const loadPostHog = () => {
    if (window.posthog) return;
    
    // PostHog initialization
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys onSessionId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
    
    // Initialize with placeholder - replace with actual PostHog key when available
    if (typeof window.posthog !== 'undefined' && window.posthog.init) {
      window.posthog.init('phc_PLACEHOLDER', { api_host: 'https://app.posthog.com' });
    }
  };

  const handleAccept = () => {
    localStorage.setItem("cookie_consent", "accepted");
    loadPostHog();
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem("cookie_consent", "declined");
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[100] bg-truvio-surface border-t border-truvio-border"
      data-testid="cookie-consent-banner"
    >
      <div className="container-main py-4 md:py-5 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-truvio-muted text-sm text-center md:text-left">
          We use cookies for anonymous analytics to{'\u00A0'}improve this platform. No{'\u00A0'}personal data is{'\u00A0'}sold or{'\u00A0'}shared.
        </p>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={handleDecline}
            className="px-5 py-2.5 text-sm font-medium text-truvio-muted border border-truvio-border hover:border-truvio-muted hover:text-truvio-text transition-colors duration-200 rounded-sm"
            data-testid="cookie-decline-btn"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="btn-primary text-sm py-2.5"
            data-testid="cookie-accept-btn"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

// Hamburger Menu Icon
const HamburgerIcon = ({ isOpen }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    {isOpen ? (
      <>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </>
    ) : (
      <>
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </>
    )}
  </svg>
);

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <motion.nav 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 w-full z-50 backdrop-blur-md border-b border-truvio-border bg-truvio-bg/80"
      data-testid="navigation"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 flex items-center justify-between py-4">
        <Link to="/" className="font-playfair text-xl md:text-2xl font-black text-truvio-text flex-shrink-0" data-testid="logo">
          Truv<span className="italic text-truvio-primary">io</span>
        </Link>
        
        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          <span className="pill-tag">In development</span>
          <Link 
            to="/manifesto" 
            className="text-truvio-muted hover:text-truvio-accent transition-colors text-sm flex items-center gap-1 whitespace-nowrap"
            data-testid="manifesto-link"
          >
            Read our manifesto <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Mobile hamburger button */}
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-truvio-text p-2"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          <HamburgerIcon isOpen={mobileMenuOpen} />
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden border-t border-truvio-border bg-truvio-surface"
        >
          <div className="max-w-[1200px] mx-auto px-6 py-4 flex flex-col gap-4">
            <span className="pill-tag w-fit">In development</span>
            <Link 
              to="/manifesto" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-truvio-muted hover:text-truvio-accent transition-colors text-sm flex items-center gap-1"
            >
              Read our manifesto <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

// Waitlist Form Component with DB storage
const WaitlistForm = ({ userType, onClose }) => {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/waitlist`, {
        email,
        role: userType,
        company_name: userType === "business" ? company : null
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Error joining waitlist:", error);
      setSubmitted(true); // Show success anyway for UX
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-6 md:py-8 px-4"
        data-testid="form-success"
      >
        <p className="font-playfair text-xl md:text-2xl italic text-truvio-accent mb-3">You're early. That matters.</p>
        <p className="text-truvio-muted text-sm md:text-base">We invite beta users personally — you'll hear from us.</p>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      onSubmit={handleSubmit}
      className="mt-6 md:mt-8 space-y-4 md:space-y-6 max-w-md mx-auto w-full px-4 md:px-0"
      data-testid="waitlist-form"
    >
      <input type="hidden" name="role" value={userType} />
      <input
        type="email"
        placeholder={userType === "business" ? "work@company.com" : "your@email.com"}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="input-minimal text-base md:text-lg"
        data-testid="email-input"
      />
      {userType === "business" && (
        <input
          type="text"
          placeholder="Your company name"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          required
          className="input-minimal text-base md:text-lg"
          data-testid="company-input"
        />
      )}
      <div className="flex gap-3 md:gap-4">
        <button 
          type="submit" 
          disabled={loading}
          className={`flex-1 text-sm md:text-base py-3 md:py-4 ${userType === "business" ? "btn-lime" : "btn-primary"}`}
          data-testid="submit-btn"
        >
          {loading ? "Joining..." : "Join the list"}
        </button>
        <button 
          type="button" 
          onClick={onClose} 
          className="btn-secondary px-4 md:px-6 text-sm md:text-base py-3 md:py-4"
          data-testid="cancel-btn"
        >
          Cancel
        </button>
      </div>
    </motion.form>
  );
};

// Hero Section
const HeroSection = () => {
  const [showForm, setShowForm] = useState(false);
  const [userType, setUserType] = useState(null);

  const handleCTA = (type) => {
    setUserType(type);
    setShowForm(true);
  };

  return (
    <section className="min-h-screen flex flex-col justify-center items-center text-center relative pt-20 md:pt-24 pb-12 md:pb-16 px-4" data-testid="hero-section">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="max-w-5xl mx-auto w-full"
      >
        {/* Eyebrow */}
        <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mb-10 md:mb-12">
          <span className="w-1.5 h-1.5 rounded-full bg-truvio-primary"></span>
          <span className="text-truvio-muted text-[0.6875rem] md:text-xs uppercase tracking-[0.2em] font-medium">Coming soon</span>
        </motion.div>

        {/* Main Headline */}
        <motion.h1 variants={fadeUp} className="font-playfair font-black hero-headline mb-10 md:mb-12" data-testid="hero-headline">
          Not opinions.<br className="sm:hidden" /> <span className="italic text-truvio-accent">Facts.</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p variants={fadeUp} className="text-lg md:text-xl lg:text-2xl text-truvio-text/90 font-epilogue font-light max-w-3xl mx-auto mb-6 md:mb-8 px-2 leading-relaxed">
          The first review platform where every review is proven<span className="hidden md:inline"> —</span><br className="hidden md:block" /><span className="md:hidden"> — </span>by identity, evidence, and community verification.
        </motion.p>

        {/* Problem statement */}
        <motion.p variants={fadeUp} className="text-truvio-muted text-sm md:text-base max-w-2xl mx-auto mb-12 md:mb-16 px-2 leading-relaxed">
          Review platforms are broken. Businesses buy their ratings. Consumers weaponise reviews. Nobody trusts what they read.
        </motion.p>

        {/* CTAs */}
        {!showForm ? (
          <motion.div variants={fadeUp} className="flex flex-col gap-5 md:gap-8 justify-center items-center w-full max-w-md mx-auto md:max-w-none md:flex-row">
            <div className="text-center w-full md:w-auto">
              <button 
                onClick={() => handleCTA("reviewer")} 
                className="btn-primary w-full md:w-auto"
                data-testid="join-reviewer-btn"
              >
                Join as reviewer
              </button>
              <p className="text-truvio-muted/70 text-[0.6875rem] mt-3 tracking-wide">Free · Takes 30{'\u00A0'}seconds · No full KYC required to{'\u00A0'}start</p>
            </div>
            <div className="text-center w-full md:w-auto">
              <button 
                onClick={() => handleCTA("business")} 
                className="btn-secondary w-full md:w-auto"
                data-testid="register-business-btn"
              >
                Register your business
              </button>
              <p className="text-truvio-muted/70 text-[0.6875rem] mt-3 tracking-wide">Early access · Verified presence from day one</p>
            </div>
          </motion.div>
        ) : (
          <WaitlistForm userType={userType} onClose={() => setShowForm(false)} />
        )}
      </motion.div>
    </section>
  );
};

// Ticker/Marquee Section
const TickerSection = () => {
  const items = [
    "Identity verified",
    "Evidence required", 
    "Rating never for sale",
    "Community moderated",
    "GDPR protected",
    "Open source rules",
    "Negative reviews welcome",
    "Fake attacks are not",
    "Data by court order only",
    "Not opinions — facts"
  ];

  const tickerContent = items.map((item, idx) => (
    <span key={idx} className="ticker-item">
      {item}
      <span className="ticker-dot"></span>
    </span>
  ));

  return (
    <div className="ticker-wrapper" data-testid="ticker-section">
      <div className="ticker-track">
        <div className="ticker-content">
          {tickerContent}
          {tickerContent}
        </div>
      </div>
    </div>
  );
};

// Problem Block Section
const ProblemSection = () => (
  <section className="section-spacing bg-truvio-surface relative" data-testid="problem-section">
    <div className="container-main">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <motion.h2 
          variants={fadeUp}
          className="font-playfair font-black text-2xl sm:text-4xl md:text-5xl lg:text-6xl italic text-center mb-12 md:mb-20 px-2 leading-tight"
        >
          The review system is broken.<br className="hidden sm:block" /> From both sides.
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-12 md:mb-20">
          {/* Panel 1 */}
          <motion.div 
            variants={fadeUp}
            className="bg-truvio-bg border border-truvio-border p-8 md:p-14"
            data-testid="problem-panel-1"
          >
            <span className="warning-tag mb-6 md:mb-8 inline-block">Side one</span>
            <h3 className="font-playfair font-black text-xl md:text-2xl lg:text-3xl mb-4 md:mb-6 leading-tight">
              Businesses learned to buy their way out.
            </h3>
            <p className="text-truvio-muted leading-relaxed text-sm md:text-base">
              Platforms that monetise through business subscriptions have a{'\u00A0'}structural conflict of{'\u00A0'}interest. 
              Negative reviews disappear for paying customers. The rating becomes a{'\u00A0'}product, not a{'\u00A0'}signal.
            </p>
          </motion.div>

          {/* Panel 2 */}
          <motion.div 
            variants={fadeUp}
            className="bg-truvio-bg border border-truvio-border p-8 md:p-14"
            data-testid="problem-panel-2"
          >
            <span className="warning-tag mb-6 md:mb-8 inline-block">Side two</span>
            <h3 className="font-playfair font-black text-xl md:text-2xl lg:text-3xl mb-4 md:mb-6 leading-tight">
              Consumers learned to{'\u00A0'}weaponise reviews.
            </h3>
            <p className="text-truvio-muted leading-relaxed text-sm md:text-base">
              Dedicated communities publish step-by-step guides on{'\u00A0'}using review platforms as{'\u00A0'}leverage{'\u00A0'}— 
              file a{'\u00A0'}complaint, threaten more, collect the refund. First-party fraud at{'\u00A0'}scale.
            </p>
          </motion.div>
        </div>

        <motion.p 
          variants={fadeUp}
          className="font-playfair font-black text-xl sm:text-2xl md:text-3xl lg:text-4xl italic text-center text-truvio-text max-w-4xl mx-auto px-4 leading-tight"
        >
          The result: a{'\u00A0'}system nobody trusts{'\u00A0'}— but everyone still uses.
        </motion.p>
      </motion.div>
    </div>
  </section>
);

// Solution Section - Three Principles with Phosphor Icons
const SolutionSection = () => {
  const principles = [
    {
      iconClass: "ph-bold ph-identification-card",
      title: "Identity",
      description: "Every reviewer is verified before writing a\u00A0single word. You stand behind your words\u00A0— because they are your words."
    },
    {
      iconClass: "ph-bold ph-file-text",
      title: "Evidence",
      description: "Every claim must be proven. Receipts. Bank statements. Screenshots. No\u00A0proof\u00A0— no\u00A0publication."
    },
    {
      iconClass: "ph-bold ph-users-three",
      title: "Community",
      description: "No algorithms. No paid decisions. Independent community moderators verify evidence and vote\u00A0— without seeing each other's decisions."
    }
  ];

  return (
    <section className="section-spacing" data-testid="solution-section">
      <div className="container-main">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeUp}
            className="font-playfair font-black text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-center mb-12 md:mb-20 px-2 leading-tight"
          >
            We didn't fix reviews.<br className="hidden sm:block" /> We changed the foundation.
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {principles.map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeUp}
                className="bg-truvio-surface border border-truvio-border p-8 md:p-12 green-border-accent card-glow"
                data-testid={`principle-card-${idx}`}
              >
                <div className="mb-6 md:mb-8">
                  <i className={`${item.iconClass}`} style={{ fontSize: '32px', color: '#1a7a4a', display: 'block' }}></i>
                </div>
                <h3 className="font-playfair font-black text-xl md:text-2xl mb-4 md:mb-5">{item.title}</h3>
                <p className="text-truvio-muted leading-relaxed text-sm md:text-base">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section - Enhanced with cards
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      icon: "device-mobile",
      title: "Start with email and\u00A0phone",
      description: "No full KYC required to\u00A0begin. Takes 30\u00A0seconds. Full verification unlocks full access\u00A0— at\u00A0no\u00A0cost."
    },
    {
      number: "02",
      icon: "paperclip",
      title: "Write and\u00A0attach proof",
      description: "Receipts · screenshots · bank statements. Evidence is\u00A0what separates a\u00A0review from an\u00A0opinion."
    },
    {
      number: "03",
      icon: "seal-check",
      title: "Your review is\u00A0verified",
      description: "New accounts and negative reviews go through community moderation. Fake claims don't pass. Real ones do."
    }
  ];

  return (
    <section className="section-spacing bg-truvio-surface" data-testid="how-it-works-section">
      <div className="container-main">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeUp}
            className="font-playfair font-black text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-center mb-12 md:mb-20 px-2 leading-tight"
          >
            Start in 30 seconds.<br className="hidden sm:block" /> Grow into a trusted voice.
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                variants={fadeUp}
                className="step-card bg-truvio-bg border border-truvio-border p-8 md:p-10 relative overflow-hidden"
                data-testid={`step-card-${idx}`}
              >
                {/* Large background number */}
                <span className="step-bg-number font-playfair font-black">{step.number}</span>
                
                {/* Icon */}
                <div className="relative z-10 mb-6">
                  <PhosphorIcon name={step.icon} className="text-3xl md:text-4xl text-truvio-primary" />
                </div>
                
                {/* Content */}
                <div className="relative z-10">
                  <span className="text-truvio-accent text-xs font-medium tracking-wider mb-3 block">STEP {step.number}</span>
                  <h3 className="font-playfair font-black text-lg md:text-xl lg:text-2xl mb-4 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-truvio-muted leading-relaxed text-sm md:text-base">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Trust/Credibility Section with Phosphor Icons
const TrustSection = () => {
  const trustItems = [
    {
      iconClass: "ph-bold ph-shield-check",
      text: "GDPR compliant · EU-based · Data stored in\u00A0the\u00A0EU"
    },
    {
      iconClass: "ph-bold ph-scales",
      text: "Identity disclosed only by\u00A0court order\u00A0— never by\u00A0business request"
    },
    {
      iconClass: "ph-bold ph-prohibit",
      text: "No subscription, no\u00A0payment changes a\u00A0rating or\u00A0moderation outcome. Ever."
    },
    {
      iconClass: "ph-bold ph-git-branch",
      text: "Open source rules · Every decision is\u00A0public · Full audit trail"
    }
  ];

  return (
    <section className="section-spacing" data-testid="trust-section">
      <div className="container-main">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeUp}
            className="font-playfair font-black text-2xl sm:text-4xl md:text-5xl lg:text-6xl italic text-center mb-12 md:mb-20 px-2 leading-tight"
          >
            Built for trust.<br className="hidden sm:block" /> Designed to be fair.
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {trustItems.map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeUp}
                className="flex flex-col items-center text-center p-6 md:p-8 bg-truvio-surface border border-truvio-border"
                data-testid={`trust-item-${idx}`}
              >
                <div 
                  className="mb-4 md:mb-5 flex items-center justify-center"
                  style={{ 
                    background: 'rgba(26,122,74,0.15)', 
                    borderRadius: '10px', 
                    padding: '12px',
                    width: '56px',
                    height: '56px'
                  }}
                >
                  <i className={item.iconClass} style={{ fontSize: '32px', color: '#1a7a4a', display: 'block' }}></i>
                </div>
                <p className="text-truvio-text/90 text-xs md:text-sm leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Final CTA Section with Phosphor Icons
const FinalCTASection = () => {
  const [showForm, setShowForm] = useState(false);
  const [userType, setUserType] = useState(null);

  const handleCTA = (type) => {
    setUserType(type);
    setShowForm(true);
  };

  return (
    <section className="section-spacing bg-truvio-surface" data-testid="final-cta-section">
      <div className="container-main">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeUp}
            className="font-playfair font-black text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-center mb-12 md:mb-20 px-2 leading-tight"
          >
            Be part of the first platform<br className="hidden sm:block" /> where reviews mean something.
          </motion.h2>

          {!showForm ? (
            <motion.div variants={fadeUp} className="grid md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
              {/* Reviewers Card */}
              <div className="bg-truvio-bg border-l-2 border-l-truvio-primary border border-truvio-border p-8 md:p-12" data-testid="reviewer-cta-card">
                <div className="flex items-center gap-3 mb-6 md:mb-8">
                  <PhosphorIcon name="medal" className="text-2xl text-truvio-primary" />
                  <h3 className="font-playfair font-black text-xl md:text-2xl">For reviewers</h3>
                </div>
                <ul className="space-y-4 md:space-y-5 mb-8 md:mb-10 text-truvio-muted text-sm md:text-base">
                  <li className="flex items-start gap-3">
                    <span className="text-truvio-primary mt-0.5">—</span>
                    Build a{'\u00A0'}verified reputation that grows in{'\u00A0'}value
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-truvio-primary mt-0.5">—</span>
                    Your vote directly decides what gets published
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-truvio-primary mt-0.5">—</span>
                    Get early access{'\u00A0'}— beta users are invited personally
                  </li>
                </ul>
                <button 
                  onClick={() => handleCTA("reviewer")} 
                  className="btn-primary w-full"
                  data-testid="final-reviewer-btn"
                >
                  Join as reviewer
                </button>
              </div>

              {/* Business Card */}
              <div className="bg-truvio-bg border-l-2 border-l-truvio-accent border border-truvio-border p-8 md:p-12" data-testid="business-cta-card">
                <div className="flex items-center gap-3 mb-6 md:mb-8">
                  <PhosphorIcon name="buildings" className="text-2xl text-truvio-accent" />
                  <h3 className="font-playfair font-black text-xl md:text-2xl">For businesses</h3>
                </div>
                <ul className="space-y-4 md:space-y-5 mb-8 md:mb-10 text-truvio-muted text-sm md:text-base">
                  <li className="flex items-start gap-3">
                    <span className="text-truvio-accent mt-0.5">—</span>
                    Protect your reputation from coordinated fake attacks
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-truvio-accent mt-0.5">—</span>
                    Be present from day one{'\u00A0'}— before your competitors
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-truvio-accent mt-0.5">—</span>
                    Verified presence that customers can trust
                  </li>
                </ul>
                <button 
                  onClick={() => handleCTA("business")} 
                  className="btn-lime w-full"
                  data-testid="final-business-btn"
                >
                  Register your business
                </button>
              </div>
            </motion.div>
          ) : (
            <div className="max-w-md mx-auto">
              <WaitlistForm userType={userType} onClose={() => setShowForm(false)} />
            </div>
          )}

          <motion.p 
            variants={fadeUp}
            className="text-center text-truvio-muted/70 text-[0.6875rem] md:text-xs mt-10 md:mt-14 tracking-wide"
          >
            Early access · Limited beta · No spam · We'll reach out personally
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => (
  <footer className="py-10 md:py-16 border-t border-truvio-border" data-testid="footer">
    <div className="container-main">
      <div className="flex flex-col gap-8 md:flex-row md:justify-between md:items-center md:gap-8">
        <Link to="/" className="font-playfair text-xl md:text-2xl font-black text-truvio-text">
          Truv<span className="italic text-truvio-primary">io</span>
        </Link>
        
        <nav className="flex items-center gap-8 md:gap-10 text-xs md:text-sm">
          <Link to="/manifesto" className="text-truvio-muted hover:text-truvio-text transition-colors duration-300" data-testid="footer-manifesto">
            Manifesto
          </Link>
          <Link to="/privacy" className="text-truvio-muted hover:text-truvio-text transition-colors duration-300" data-testid="footer-privacy">
            Privacy
          </Link>
          <a href="mailto:hello@truvio.me" className="text-truvio-muted hover:text-truvio-text transition-colors duration-300" data-testid="footer-contact">
            Contact
          </a>
        </nav>

        <div className="text-left md:text-right">
          <p className="text-truvio-muted/70 text-[0.6875rem] md:text-xs tracking-wide">
            © 2026 Truvio · Registered in Cyprus · Data stored in the EU
          </p>
          <p className="text-truvio-muted/40 text-[0.625rem] mt-1.5 italic tracking-wide">
            truvio.me
          </p>
        </div>
      </div>
    </div>
  </footer>
);

// Home Page Component
function HomePage() {
  useEffect(() => {
    document.title = "Truvio — Verified Reviews You Can Trust";
  }, []);

  return (
    <div className="bg-truvio-bg min-h-screen relative">
      {/* Ambient glows */}
      <div className="glow-green"></div>
      <div className="glow-lime"></div>
      
      {/* Grain overlay */}
      <div className="grain-overlay"></div>
      
      {/* Content */}
      <div className="relative z-10">
        <Navigation />
        <HeroSection />
        <TickerSection />
        <ProblemSection />
        <SolutionSection />
        <HowItWorksSection />
        <TrustSection />
        <FinalCTASection />
        <Footer />
      </div>
    </div>
  );
}

// Main App Component with Router
function App() {
  return (
    <Router>
      <CookieConsentBanner />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/manifesto" element={<ManifestoPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </Router>
  );
}

export default App;
